import type {
  Action,
  ValidationError,
  ValidationResult,
} from "../../types/actions";
import type { Project } from "../../types/project";
import type { Clip, Effect } from "../../types/timeline";
import type {
  MutableClip,
  MutableTimeline,
  MutableTrack,
} from "../../utils/immutable-updates";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler, ActionHandlerContext } from "../registry";
import { findClip } from "./clip-helpers";

/**
 * The six clip-effect types the executor's switch carried. effect/setOrder and
 * effect/setStack share the prefix but not the shape and already live in
 * bulk-set.ts.
 *
 * Each matching clip recomputes its stack from its own array rather than going
 * through patchClip, which would compute one replacement stack and write it to
 * every clip carrying the id. That difference only shows on a project that has
 * ended up with a duplicated clip id, and this is a move, not a fix.
 */
function mapEffects(
  project: Project,
  clipId: string,
  fn: (effects: Effect[]) => Effect[],
): void {
  const timeline = project.timeline as MutableTimeline;
  timeline.tracks = timeline.tracks.map((track: MutableTrack) => ({
    ...track,
    clips: track.clips.map((clip: MutableClip) =>
      clip.id === clipId ? { ...clip, effects: fn([...clip.effects]) } : clip,
    ),
  }));
}

interface EffectTarget {
  readonly clip: Clip | null;
  readonly errors: ValidationError[];
}

function checkTarget(action: Action, project: Project): EffectTarget {
  const clipId = (action.params as { clipId?: unknown }).clipId;
  if (!clipId || typeof clipId !== "string") {
    return {
      clip: null,
      errors: [
        {
          code: "INVALID_PARAMS",
          message: "Clip ID is required and must be a string",
          path: "params.clipId",
        },
      ],
    };
  }

  const clip = findClip(project, clipId);
  if (!clip) {
    return {
      clip: null,
      errors: [
        {
          code: "CLIP_NOT_FOUND",
          message: `Clip with ID ${clipId} not found`,
          path: "params.clipId",
        },
      ],
    };
  }

  const errors: ValidationError[] = [];
  const track = project.timeline.tracks.find((t) => t.id === clip.trackId);
  if (track?.locked) {
    errors.push({
      code: "TRACK_LOCKED",
      message: `Track containing clip ${clipId} is locked`,
      path: "params.clipId",
    });
  }
  return { clip, errors };
}

function validateTarget(
  action: Action,
  project: Project,
  extra: (action: Action, clip: Clip) => ValidationError[],
): ValidationResult {
  const target = checkTarget(action, project);
  // A missing or unknown clip stops the check there, so the caller never sees
  // an effectType or effectId complaint about a clip that is not addressable.
  if (!target.clip) return { valid: false, errors: target.errors };
  const errors = [...target.errors, ...extra(action, target.clip)];
  return { valid: errors.length === 0, errors };
}

function checkEffectId(action: Action, clip: Clip): ValidationError[] {
  const effectId = (action.params as { effectId?: unknown }).effectId;
  if (!effectId || typeof effectId !== "string") {
    return [
      {
        code: "INVALID_PARAMS",
        message: "Effect ID is required and must be a string",
        path: "params.effectId",
      },
    ];
  }
  if (!clip.effects.some((e) => e.id === effectId)) {
    return [
      {
        code: "EFFECT_NOT_FOUND",
        message: `Effect with ID ${effectId} not found on clip`,
        path: "params.effectId",
      },
    ];
  }
  return [];
}

function invertOnClip(
  action: Action,
  projectBefore: Project,
  build: (clipId: string, clip: Clip) => Action | null,
): Action | null {
  const clipId = (action.params as { clipId: string }).clipId;
  const clip = findClip(projectBefore, clipId);
  if (!clip) return null;
  return build(clipId, clip);
}

function findEffect(clip: Clip, effectId: unknown): Effect | undefined {
  return clip.effects.find((e) => e.id === effectId);
}

const effectAdd: ActionHandler = {
  type: "effect/add",

  validate(action: Action, project: Project): ValidationResult {
    return validateTarget(action, project, (a) => {
      const effectType = (a.params as { effectType?: unknown }).effectType;
      if (!effectType || typeof effectType !== "string") {
        return [
          {
            code: "INVALID_PARAMS",
            message: "Effect type is required and must be a string",
            path: "params.effectType",
          },
        ];
      }
      return [];
    });
  },

  apply(action: Action, project: Project, ctx: ActionHandlerContext): void {
    const params = action.params as {
      clipId: string;
      effectType: string;
      params?: Record<string, unknown>;
      effectId?: string;
      index?: number;
      enabled?: boolean;
    };
    const newEffect: Effect = {
      id: params.effectId ?? `effect-${crypto.randomUUID()}`,
      type: params.effectType,
      params: params.params || {},
      enabled: params.enabled ?? true,
    };
    // Redo replays this same action object; without this it would mint a fresh
    // id and the stored inverse would target the one from the first run.
    (action.params as Record<string, unknown>).effectId = newEffect.id;

    mapEffects(project, params.clipId, (effects) => {
      const index = Number.isInteger(params.index)
        ? Math.max(0, Math.min(effects.length, params.index!))
        : effects.length;
      effects.splice(index, 0, newEffect);
      return effects;
    });
    ctx.lastAddedIds.set("effect", newEffect.id);
  },

  invert(action: Action, projectBefore: Project): Action | null {
    return invertOnClip(action, projectBefore, (clipId) => {
      // The inverse is built before apply runs, so an add that mints its own id
      // has none to name yet. The executor swaps the marker for the id apply
      // recorded, while it is still the last effect added.
      const effectId = (action.params as { effectId?: string }).effectId;
      return createInverseAction(action, "effect/remove", {
        clipId,
        effectId: effectId ?? "__LAST_ADDED__",
      });
    });
  },
};

const effectRemove: ActionHandler = {
  type: "effect/remove",

  validate(action: Action, project: Project): ValidationResult {
    return validateTarget(action, project, checkEffectId);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { clipId: string; effectId: string };
    mapEffects(project, params.clipId, (effects) =>
      effects.filter((e) => e.id !== params.effectId),
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    return invertOnClip(action, projectBefore, (clipId, clip) => {
      const effectId = (action.params as { effectId: string }).effectId;
      const effect = findEffect(clip, effectId);
      if (!effect) return null;

      const index = clip.effects.findIndex((e) => e.id === effectId);

      return createInverseAction(action, "effect/restore", {
        clipId,
        effect: { ...effect },
        index,
      });
    });
  },
};

/**
 * The inverse of a remove, so it carries the effect and the index it sat at.
 * The legacy validator had no case for it, and the inverse generator had none
 * either: undo and redo apply an inverse without asking for one back, so the
 * gap only bites a restore executed through the executor, which then reports
 * "No inverse action available" and leaves the effect in place. It gets one
 * here for the same reason marker/restore did, and validate stays at the
 * clip-only check the legacy preamble performed.
 */
const effectRestore: ActionHandler = {
  type: "effect/restore",

  validate(action: Action, project: Project): ValidationResult {
    return validateTarget(action, project, () => []);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      effect: Effect;
      index: number;
    };
    mapEffects(project, params.clipId, (effects) => {
      effects.splice(params.index, 0, params.effect);
      return effects;
    });
  },

  invert(action: Action, projectBefore: Project): Action | null {
    return invertOnClip(action, projectBefore, (clipId) => {
      const effect = (action.params as { effect?: Effect }).effect;
      if (!effect || typeof effect.id !== "string") return null;
      return createInverseAction(action, "effect/remove", {
        clipId,
        effectId: effect.id,
      });
    });
  },
};

const effectUpdate: ActionHandler = {
  type: "effect/update",

  validate(action: Action, project: Project): ValidationResult {
    return validateTarget(action, project, checkEffectId);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      effectId: string;
      params: Record<string, unknown>;
    };
    mapEffects(project, params.clipId, (effects) =>
      effects.map((e) =>
        e.id === params.effectId
          ? { ...e, params: { ...e.params, ...params.params } }
          : e,
      ),
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    return invertOnClip(action, projectBefore, (clipId, clip) => {
      const effectId = (action.params as { effectId: string }).effectId;
      const effect = findEffect(clip, effectId);
      if (!effect) return null;

      return createInverseAction(action, "effect/update", {
        clipId,
        effectId,
        params: { ...effect.params },
      });
    });
  },
};

const effectToggle: ActionHandler = {
  type: "effect/toggle",

  validate(action: Action, project: Project): ValidationResult {
    return validateTarget(action, project, (a, clip) => {
      const errors = checkEffectId(a, clip);
      if (typeof (a.params as { enabled?: unknown }).enabled !== "boolean") {
        errors.push({
          code: "INVALID_PARAMS",
          message: "Enabled parameter must be a boolean",
          path: "params.enabled",
        });
      }
      return errors;
    });
  },

  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      effectId: string;
      enabled: boolean;
    };
    mapEffects(project, params.clipId, (effects) =>
      effects.map((e) =>
        e.id === params.effectId ? { ...e, enabled: params.enabled } : e,
      ),
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    return invertOnClip(action, projectBefore, (clipId, clip) => {
      const effectId = (action.params as { effectId: string }).effectId;
      const effect = findEffect(clip, effectId);
      if (!effect) return null;

      return createInverseAction(action, "effect/toggle", {
        clipId,
        effectId,
        enabled: effect.enabled,
      });
    });
  },
};

const effectReorder: ActionHandler = {
  type: "effect/reorder",

  validate(action: Action, project: Project): ValidationResult {
    return validateTarget(action, project, (a, clip) => {
      const errors = checkEffectId(a, clip);
      const newIndex = (a.params as { newIndex?: unknown }).newIndex;
      if (
        typeof newIndex !== "number" ||
        newIndex < 0 ||
        newIndex >= clip.effects.length
      ) {
        errors.push({
          code: "INVALID_PARAMS",
          message: `New index must be between 0 and ${clip.effects.length - 1}`,
          path: "params.newIndex",
        });
      }
      return errors;
    });
  },

  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      effectId: string;
      newIndex: number;
    };
    mapEffects(project, params.clipId, (effects) => {
      const effectIndex = effects.findIndex((e) => e.id === params.effectId);
      if (effectIndex !== -1) {
        const [effect] = effects.splice(effectIndex, 1);
        effects.splice(params.newIndex, 0, effect);
      }
      return effects;
    });
  },

  invert(action: Action, projectBefore: Project): Action | null {
    return invertOnClip(action, projectBefore, (clipId, clip) => {
      const effectId = (action.params as { effectId: string }).effectId;
      const currentIndex = clip.effects.findIndex((e) => e.id === effectId);
      if (currentIndex === -1) return null;

      return createInverseAction(action, "effect/reorder", {
        clipId,
        effectId,
        newIndex: currentIndex,
      });
    });
  },
};

for (const handler of [
  effectAdd,
  effectRemove,
  effectRestore,
  effectUpdate,
  effectToggle,
  effectReorder,
]) {
  registerActionHandler(handler);
}
